var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.studio",
  title: "EGT* Studio page",
  description: "EGT* Studio — the connected AI film studio, presented as a cinematic 'Coming Soon' waitlist microsite (the whole film, the same characters, yours).",
  supportServer: true,
  fields: { reserveUrl: { title: "Reserve URL", type: "uri", description: "Reservation entry point.", required: false } }
});
var S = require("../helpers/egt.sections.js");
var FS = require("../helpers/egt.filmstudio.js");

/* ---------------------------------------------------------------------------
 * EGT* Film Studio — the launch surface (rebuilt from the founder's notes,
 * 2026-10-24; REPLACES the 2026-07-29 "Coming Soon" page, which replaced /voice).
 *
 * Arc: hero (varied intro scenes + Request Access) → the story (script →
 * character → scenes → video, hand-drawn, scroll-scrubbed both ways) → the
 * asterisk lands on EGT* + the slogan → Enter the Booth (the real motion-capture
 * test, voice-only and motion-capture side by side) → the models it integrates
 * with → Real AI Magic (the short film + the intro as video cards) → the ask
 * again → the turn → continuity, kept → one connected pipeline → not a clip, a
 * whole studio → made with AI, hidden from no one → footnotes.
 *
 * Request Access hands the email to the platform reserve wizard on /reserve
 * (EGT* is part of the platform). The model section is the one place that names
 * other models (founder ruling 2026-10-24), each footnoted as not endorsed by or
 * affiliated with EGT; everywhere else only "Intrepid" is named.
 *
 * BRAND (agents.md §4): frosted glass over UNDIMMED media (never a scrim);
 * true-black + scarce gold via CSS vars; Playfair + Inter; rem only; no AI-slop.
 * Behaviour: /src/egt.filmstudio.js (shared with the homepage hero) and
 * /src/egt.studio.js (continuity, the turn, the script reader, the quote).
 * ------------------------------------------------------------------------- */

/* The launch-phase resolution of the wordmark asterisk. Deduped once at the page
 * foot (FN.list) AND printed in full as the clickable star popover + the capture
 * fine-print. The MEANING of the * is an open founder decision (O-2) — this is
 * the CMO-recommended "honest footnote" reading, shipped now, not presented as
 * final. Post-launch it re-points to "your results, your rights" (founder-gated). */
var STAR_LINE = "Public beta. Samples on this page were made with a pre-release version of the studio, and features may change.";

/* The clickable wordmark asterisk: a REAL <button> (keyboard-operable, aria-
 * expanded) that reveals a small frosted popover carrying STAR_LINE. Disclosure
 * is the brand's signature gesture, not buried fine-print. Rendered at the hero
 * eyebrow, the honesty section, and the capture eyebrow. */
function star() {
  return {
    n: "span", c: "egt-studio-star__wrap", "data-egt-star-wrap": "true",
    i: [
      { n: "button", type: "button", c: "egt-studio-star", "data-egt-star": "true",
        "aria-expanded": "false", "aria-label": "What the asterisk means", i: "*" },
      { n: "span", c: "egt-studio-star__pop", "data-egt-star-pop": "true", role: "status", "aria-live": "polite",
        i: [ { n: "span", c: "egt-studio-star__pop-mark", "aria-hidden": "true", i: "*" }, " " + STAR_LINE ] }
    ]
  };
}

/* The wordmark for eyebrows: "EGT" + the clickable asterisk + " Studio", so the
 * star sits right after "EGT" — rendering "EGT* Studio" (matches the homepage/nav
 * mark). The star's decorative glyph is aria-hidden and the button carries its own
 * aria-label, so a screen reader hears "EGT … Studio", never "EGT star Studio". */
function wordmark() { return [ { n: "span", c: "egt-mark", i: [ "EGT", star() ] }, " Film Studio" ]; }

/* A small "Powered by Intrepid" lockup (the one nameable partner). */
function poweredByIntrepid() {
  return {
    n: "div", c: "egt-studio-intrepid",
    i: [
      { n: "span", c: "egt-studio-intrepid__t", i: "Powered by" },
      { n: "img", c: "egt-studio-intrepid__mark", src: "/imgs/intrepid-mark.svg",
        alt: "Intrepid", loading: "lazy", decoding: "async" }
    ]
  };
}

/* The frosted cinematic "scene viewer" — the house frosted-glass primitive as a
 * film frame. A real still fills the frame (data-egt-scene-img, server-rendered
 * for the initial scene so it shows JS-off and on first paint); the client
 * cross-fades a new still + caption per selection. NEVER a fabricated live render
 * — the still is a real pre-made asset that MATCHES its caption. */
function sceneViewer(scope, initial) {
  initial = initial || {};
  var hasImg = !!(initial.img && String(initial.img).trim());
  return {
    n: "div", c: "egt-studio-scene", "data-egt-scene": scope || "true",
    i: {
      n: "div", c: "egt-studio-scene__frame" + (hasImg ? " has-img" : ""), "data-egt-scene-frame": "true",
      style: initial.hue != null ? "--scene-hue:" + initial.hue : undefined,
      i: [
        { n: "span", c: "egt-studio-scene__img", "data-egt-scene-img": "true", "aria-hidden": "true",
          style: hasImg ? "background-image:url(\"" + initial.img + "\")" : undefined },
        { n: "span", c: "egt-studio-scene__grain", "aria-hidden": "true", role: "presentation" },
        { n: "span", c: "egt-studio-scene__frameline", "aria-hidden": "true", role: "presentation" },
        { n: "div", c: "egt-studio-scene__body",
          i: [
            { n: "p", c: "egt-studio-scene__kicker", "data-egt-scene-kicker": "true", i: initial.kicker || "Scene" },
            { n: "p", c: "egt-studio-scene__cap", "data-egt-scene-cap": "true", "aria-live": "polite",
              i: initial.caption || "" }
          ] }
      ]
    }
  };
}

/* The honest, tap-to-play sample player (carried VERBATIM from Voice). NEVER
 * autoplays. The client plays a real cleared clip at /audio/voice/<sample>.m4a
 * when one exists; otherwise it shows the graceful "voices arriving" note. Real
 * <button> (keyboard + SR). */

exports.render = async function render(options) {
  var reserveUrl = (options && options.reserveUrl) || "/reserve";
  var FN = S.footnotes();
  var N = notes();

  /* No nav CTA here (founder 2026-10-24: "Let's try this one without a CTA in the
   * toolbar") — the hero and the second ask carry Request Access. */
  return S.page(reserveUrl, [
    studioSchema(),
    renderHero(FN, N),
    renderStory(FN, N),
    renderLanding(),
    renderBooth(FN, N),
    renderModels(FN, N),
    renderMagic(FN, N),
    renderAsk(FN, N),
    renderTurn(),
    renderContinuity(FN, N.samples),
    renderConnectedScript(FN, N.input),
    renderBreadth(),
    renderHonesty(),
    FN.list(),
    FS.script()
  ], {});
};

/* The page's footnotes (founder notes 2026-10-24), each one shared node so
 * S.footnotes() lists it once however many places mark it. */
function notes() {
  return {
    input: "Input varies, and the look and feel of what's generated isn't guaranteed.",
    samples: S.brand("Samples were made with a pre-release version of EGT* Film Studio. Features may change."),
    beta: [ "Access to the platform is in public beta, so some features may not work as expected. ",
      { n: "a", href: "/journal/what-is-a-public-beta", i: "Learn about beta and testing" },
      ". Joining is your choice: the platform is provided as-is, with no warranties, and reservations aren't refundable. Rather wait for the finished product? ",
      { n: "a", href: "#egt-cap-footer", i: "Get notified" },
      " when it's ready for general use." ],
    models: S.brand("Model names and logos belong to their owners and are shown only to name models EGT* Film Studio may integrate with. Some may be available at launch; others may need to build their own integration with us. EGT isn't affiliated with or endorsed by them, and showing a model isn't an endorsement of it or a promise that it will be available.")
  };
}

/* =====================================================================
 * HERO — a loop of varied scenes from the EGT* intro (western, noir, an eagle
 * hunter, the kelp forest, a heist, a ballroom, balloons, lions) and the ask.
 * ===================================================================== */
function renderHero(FN, N) {
  return FS.hero({
    id: "egt-studio-top", loop: "film-studio", reqLabel: [ "Request Access Today", FN.mark(N.beta) ],
    side: true, caption: S.brand("Showcase crafted with EGT* AI Film Studio"),
    title: [ "It's been done before. ", { n: "br" }, "But never with ",
      { n: "span", c: "egt__gold", i: "this level of style." } ],
    next: "#egt-fs-story"
  });
}

/* =====================================================================
 * THE STORY — script → character → scenes → video. Hand-drawn, colourful
 * icons that draw on as you scroll, a hand-drawn squiggle to the next, each
 * description fading in. Scrubbed by scroll position, so it runs backwards
 * when you scroll back up (/src/egt.filmstudio.js sets --t on each piece).
 * Reduced motion: everything shown, nothing moves.
 * ===================================================================== */
var ICONS = {
  script: '<path d="M30 18c14-1.2 30-.8 44 .4l15 14.6c.9 22 .6 46-.6 68-18 1.4-40 1.2-58 .2C29 79 28.6 42 30 18z"/><path d="M74 18.6c-.6 5-.4 10 .2 14.8 5 .4 10 .2 14.8-.4"/><path d="M41 47c11-.8 24-.6 35 .2M41 59c9-.6 20-.4 31 .2M41 71c12-.7 25-.5 36 .3M41 83c7-.4 14-.2 20 .4"/><path d="M92 54l3.6 7.4 7.6 2.2-7.4 3-2.6 7.8-3-7.6-7.6-2.6 7.4-3z"/>',
  character: '<path d="M60 22c13 0 22 10 21.6 23-.4 14-10 24-22 23.6-12-.4-21-10.6-21-23.6S47 22 60 22z"/><path d="M50 50c2.4 4.6 6 6.8 10.2 6.8 4.4 0 8-2.4 10-6.8"/><path d="M52 39.4h.4M68 39.4h.4"/><path d="M26 102c2-17 16-28 34-28s32 11 34.6 28"/><path d="M80 24c4-6 10-8 16-6M84 30c5-2 10-1 13 2"/>',
  scenes: '<path d="M22 92c8-28 22-48 38-62"/><path d="M60 30c10 5 20 15 26 26"/><path d="M38 22l14 4-4 14-14-4z" transform="rotate(-8 45 31)"/><path d="M18 100c26-2 58-2 86 0"/><path d="M74 66c8-6 18-6 26 0M70 76c10-8 24-8 34 0"/><path d="M28 58c4 2 9 2 13 0M24 70c5 2 11 2 16 0"/>',
  video: '<path d="M16 38c20-1.4 44-1.4 64 0 1.6 15 1.6 31 0 46-20 1.4-44 1.4-64 0-1.4-15-1.4-31 0-46z"/><path d="M80 52l24-12c1.4 13 1.4 27 0 40L80 70"/><path d="M41 49c7 3.4 13 7.2 18 11.6-5 4.4-11 8.2-18 11.6-.8-7.8-.8-15.4 0-23.2z"/><path d="M24 30c4-6 10-8 16-8M58 28c3-4 7-6 12-6"/>',
  silent: '<path d="M16 34c24-1.4 54-1.4 78 0 1.2 18 1.2 36 0 54-24 1.4-54 1.4-78 0-1.2-18-1.2-36 0-54z"/><path d="M16 44h78M16 78h78"/><path d="M24 36v6M36 36v6M48 36v6M60 36v6M72 36v6M84 36v6M24 80v6M36 80v6M48 80v6M60 80v6M72 80v6M84 80v6"/><path d="M98 58c6 0 10 4 10 9M104 52c8 2 12 8 12 15"/><path d="M100 76l14-16"/>',
  shortfilm: '<path d="M18 60a42 42 0 1 0 84 0a42 42 0 1 0 -84 0z"/><path d="M54 60a6 6 0 1 0 12 0a6 6 0 1 0 -12 0z"/><path d="M51 37a9 9 0 1 0 18 0a9 9 0 1 0 -18 0zM72.9 52.9a9 9 0 1 0 18 0a9 9 0 1 0 -18 0zM64.5 78.6a9 9 0 1 0 18 0a9 9 0 1 0 -18 0zM37.5 78.6a9 9 0 1 0 18 0a9 9 0 1 0 -18 0zM29.1 52.9a9 9 0 1 0 18 0a9 9 0 1 0 -18 0z"/>',
  musicvideo: '<path d="M44 82V34c14-3 28-6 40-10v46"/><path d="M44 82c0 6-6 10-12 10s-10-3-10-8 5-9 11-9 11 3 11 7z"/><path d="M84 70c0 6-6 10-12 10s-10-3-10-8 5-9 11-9 11 3 11 7z"/><path d="M44 46c14-3 28-7 40-11"/><path d="M96 30c4 2 7 6 8 11M100 22c6 3 10 9 11 16"/>'
};

/* Inline SVG as UA/JSON nodes (a markup string would be escaped as text).
 * Turns '<path d=".."/><rect ../>' into [{n:"path", d:".."}, …]. */
function svgNodes(markup, extra) {
  var out = [], re = /<(path|rect|circle)\s([^>]*?)\/>/g, m;
  while ((m = re.exec(markup))) {
    var node = { n: m[1] }, ar = /([a-zA-Z-]+)="([^"]*)"/g, a;
    while ((a = ar.exec(m[2]))) { node[a[1]] = a[2]; }
    if (extra) { Object.keys(extra).forEach(function (k) { node[k] = extra[k]; }); }
    out.push(node);
  }
  return out;
}
function svg(viewBox, width, markup, extra) {
  return { n: "svg", viewBox: viewBox, fill: "none", stroke: "currentColor", "stroke-width": width,
    "stroke-linecap": "round", "stroke-linejoin": "round", "aria-hidden": "true", focusable: "false",
    i: svgNodes(markup, extra) };
}

function icon(name, color) {
  return { n: "span", c: "egt-fs-ico", style: "--ico:" + color, "aria-hidden": "true",
    i: svg("0 0 120 120", "3.6", ICONS[name], { pathLength: "1" }) };
}

/* the hand-drawn squiggle that runs from one icon to the next */
function squiggle(dir) {
  var d = dir === "down"
    ? "M30 4c18 14-16 22 0 38s-14 24 2 38 14 18 0 34"
    : "M4 34c22-26 38 22 60 0s34-26 56 0 30 14 52-6";
  var vb = dir === "down" ? "0 0 60 120" : "0 0 176 60";
  return { n: "span", c: "egt-fs-squig egt-fs-squig--" + (dir || "across"), "aria-hidden": "true",
    i: svg(vb, "2.6", '<path d="' + d + '"/>', { pathLength: "1" }) };
}

var STEPS = [
  { k: "script", t: "Script", c: "#C48E2F", d: "Starts with a script. Magically pulling out the important stuff for you." },
  { k: "character", t: "Character", c: "#E8735A", d: "Whimsical or serious, craft them perfect." },
  { k: "scenes", t: "Scenes", c: "#3FB6A8", d: "Control the mood lighting and share them with your team." },
  { k: "video", t: "Video", c: "#4C9BE8", d: "Generate cinema-ready video with up to 10-bit precision." }
];

function renderStory(FN, N) {
  var span = 1 / STEPS.length;
  var row = [];
  STEPS.forEach(function (s, i) {
    var from = i * span, to = from + span * 0.55;
    row.push({ n: "li", c: "egt-fs-step", "data-egt-fs-from": from.toFixed(3), "data-egt-fs-to": to.toFixed(3),
      i: [
        icon(s.k, s.c),
        { n: "h3", c: "egt-fs-step__t", i: [ s.t, s.k === "video" ? FN.mark(N.input) : null ].filter(Boolean) },
        { n: "p", c: "egt-fs-step__d", i: s.d }
      ] });
    if (i < STEPS.length - 1) {
      row.push({ n: "li", c: "egt-fs-link", "aria-hidden": "true",
        "data-egt-fs-from": (from + span * 0.55).toFixed(3), "data-egt-fs-to": ((i + 1) * span).toFixed(3),
        i: [ squiggle("across"), squiggle("down") ] });
    }
  });
  return {
    n: "section", c: "egt-fs-story", id: "egt-fs-story", "data-egt-fs-scrub": "true", "data-egt-fs-duration": "5200",
    i: { n: "div", c: "egt-fs-story__stage",
      i: { n: "div", c: "egt__shell egt-fs-story__inner",
        i: [
          { n: "p", c: "egt__eyebrow egt-fs-story__eyebrow", i: "How a film comes together" },
          { n: "ol", c: "egt-fs-story__row", i: row }
        ] } }
  };
}

/* =====================================================================
 * THE LANDING — the restless asterisk spins down and lands beside EGT (white
 * Playfair, gold *), the coin logo above, then the slogan blurs in word by word
 * like the film slates. Scroll-scrubbed and reversible.
 * ===================================================================== */
function blurWords(text, from, to, cls) {
  var words = text.split(" ");
  var step = (to - from) / words.length;
  return { n: "p", c: cls, "aria-label": text,
    i: words.map(function (w, i) {
      return { n: "span", c: "egt-fs-bw", "aria-hidden": "true",
        "data-egt-fs-from": (from + i * step).toFixed(3), "data-egt-fs-to": (from + (i + 1.6) * step).toFixed(3),
        i: w + (i < words.length - 1 ? " " : "") };
    }) };
}

function renderLanding() {
  return {
    n: "section", c: "egt-fs-land", id: "egt-fs-land", "data-egt-fs-scrub": "true", "data-egt-fs-duration": "4200",
    i: { n: "div", c: "egt-fs-land__stage",
      i: { n: "div", c: "egt-fs-land__inner",
        i: [
          { n: "img", c: "egt-fs-land__coin", src: "/logo.svg", alt: "", "aria-hidden": "true",
            "data-egt-fs-from": "0.46", "data-egt-fs-to": "0.62" },
          { n: "h2", c: "egt-fs-land__mark", "aria-label": "EGT*",
            i: [
              { n: "span", c: "egt-fs-land__egt", "aria-hidden": "true",
                i: "EGT".split("").map(function (ch, i) {
                  return { n: "span", c: "egt-fs-bw", "data-egt-fs-from": (0.30 + i * 0.05).toFixed(2),
                    "data-egt-fs-to": (0.42 + i * 0.05).toFixed(2), i: ch };
                }) },
              { n: "span", c: "egt-fs-land__star", "aria-hidden": "true", "data-egt-fs-star": "true",
                "data-egt-fs-from": "0", "data-egt-fs-to": "0.5", i: "*" }
            ] },
          blurWords("Welcome to the Universe.", 0.60, 0.74, "egt-fs-land__line"),
          blurWords("A human story, crafted with AI.", 0.74, 0.90, "egt-fs-land__sub")
        ] } }
  };
}

/* =====================================================================
 * ENTER THE BOOTH — the motion-capture test (2026-10-19): one performance in a
 * voice booth, carried into three different characters two ways. Script on the
 * left (lit in time with the take), both takes side by side on the right:
 * voice only, and motion capture. Wide on desktop, vertical on phones.
 * ===================================================================== */
var BOOTH_LINES = [
  { s: 0.0, e: 10.3, t: "This is a sample test of EGT* AI Film Studio." },
  { s: 10.3, e: 18.5, t: "This is a sample test of motion capturing and the ability to equate emotions into another character." },
  { s: 18.5, e: 26.8, t: "So I picked a completely different character that looked completely different than me, so don't judge me for it." },
  { s: 26.8, e: 32.6, t: "But besides that, let's see if it really works. Let's see how good it is." },
  { s: 32.6, e: 38.4, t: "And of course, you would probably do this with a better mic than I currently have, so who knows." },
  { s: 38.4, e: 45.0, t: "What do you think? Does it work?" }
];

function renderBooth(FN, N) {
  return {
    n: "section", c: "egt-section egt-canvas egt-fs-booth", id: "egt-fs-booth",
    i: [
      S.media({ img: "/vis/film-studio/still-ax24.jpg", alt: "", parallax: 0.06 }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: { n: "div", c: "egt-glass egt-glass--wide egt-fs-booth__card egt-reveal egt-reveal--up", "data-egt-fs-booth": "true",
          i: [
            { n: "div", c: "egt-fs-booth__head",
              i: [
                { n: "p", c: "egt__eyebrow", i: "Enter the Booth" },
                { n: "h2", c: "egt-section__title",
                  i: [ "Enhance the video with ", { n: "span", c: "egt__gold", i: "real human emotion." }, FN.mark(N.samples) ] },
                { n: "p", c: "egt-section__lead",
                  i: "Never miss a dramatic stare or a reaction straight from the heart. With motion capturing, share the emotion with your character, and use your voice or a thousand professionals to give life to the story." }
              ] },
            { n: "div", c: "egt-fs-booth__grid",
              i: [
                { n: "div", c: "egt-fs-booth__script",
                  i: [
                    { n: "div", c: "egt-fs-booth__mic", "aria-hidden": "true",
                      i: svg("0 0 48 48", "2", '<rect x="17" y="6" width="14" height="24" rx="7"/><path d="M11 24c0 7 6 13 13 13s13-6 13-13M24 37v6M17 43h14"/>') },
                    { n: "p", c: "egt-fs-booth__slug", i: "In the booth · one take" },
                    { n: "div", c: "egt-fs-booth__window",
                      i: { n: "ol", c: "egt-fs-booth__lines",
                        i: BOOTH_LINES.map(function (l, i) {
                          return { n: "li", c: "egt-fs-booth__line", "data-s": String(l.s), "data-e": String(l.e), i: S.brand(l.t) };
                        }) } },
                    { n: "button", type: "button", c: "egt-fs-booth__sound", "data-egt-fs-sound": "true", "aria-pressed": "false",
                      i: [ { n: "span", c: "egt-fs-booth__sound-ico", "aria-hidden": "true" },
                        { n: "span", "data-egt-fs-sound-label": "true", i: "Play with sound" } ] }
                  ] },
                /* founder 2026-10-26: the one approved video that shows the performer
                 * with every character at once, motion capture (top row) and voice-only
                 * lip sync together; the vertical social cut on every screen (founder) */
                { n: "div", c: "egt-fs-booth__takes egt-fs-booth__takes--one",
                  i: { n: "figure", c: "egt-fs-booth__take",
                    i: [
                      { n: "div", c: "egt-fs-booth__frame egt-fs-booth__frame--compare",
                        i: { n: "video", c: "egt-fs-booth__video", muted: true, loop: true, playsinline: true, preload: "none",
                          "data-egt-fs-take": "compare", "data-wide": "/vis/film-studio/booth-compare.mp4", "data-tall": "/vis/film-studio/booth-compare.mp4",
                          "data-poster-wide": "/vis/film-studio/booth-compare.jpg", "data-poster-tall": "/vis/film-studio/booth-compare.jpg",
                          poster: "/vis/film-studio/booth-compare.jpg",
                          "aria-label": "One booth take carried into three characters: motion capture on the left, voice only on the right" } },
                      { n: "figcaption", c: "egt-fs-booth__cap", i: "Left: motion capture · Right: voice only (lip sync)" }
                    ] } }
              ] },
            { n: "p", c: "egt-studio-foot",
              i: "One performance, recorded on a phone in a booth, carried into three different characters. The real take plays in between." }
          ] } }
    ]
  };
}

/* =====================================================================
 * MODELS — the latest generative models, each in its own brand mark, blurring
 * in from nothing (no scroller, no pop-up window). Footnoted: shown to name
 * them, not endorsed by or affiliated with them.
 * ===================================================================== */
var MODELS = [
  { k: "kling", t: "Kling" }, { k: "veo", t: "Veo" }, { k: "firefly", t: "Firefly" }, { k: "runway", t: "Runway" },
  { k: "luma", t: "Luma Ray" }, { k: "pika", t: "Pika" }, { k: "hailuo", t: "Hailuo" }, { k: "seedance", t: "Seedance" },
  { k: "wan", t: "Wan" }, { k: "vidu", t: "Vidu" }, { k: "ltx", t: "LTX" }, { k: "midjourney", t: "Midjourney" }
];

function renderModels(FN, N) {
  return {
    n: "section", c: "egt-section egt-fs-models", id: "egt-fs-models",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-fs-models__head egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "Model-agnostic" },
            { n: "h2", c: "egt-section__title",
              i: [ "Integrate with the latest ", { n: "span", c: "egt__gold", i: "generative models." }, FN.mark(N.models) ] }
          ] },
        { n: "ul", c: "egt-fs-models__grid", "data-egt-fs-models": "true",
          i: MODELS.map(function (m, i) {
            return { n: "li", c: "egt-fs-model", style: "--i:" + i,
              i: [ { n: "img", c: "egt-fs-model__logo", src: "/imgs/models/" + m.k + ".svg", alt: "", loading: "lazy", decoding: "async" },
                { n: "span", c: "egt-fs-model__t", i: m.t } ] };
          }) },
        { n: "p", c: "egt-fs-models__close egt-reveal", i: [ "Some may be ready at launch; others will build their integration with us. ", { n: "span", c: "egt__gold", i: "Intrepid" }, " directs the rest." ] }
      ] }
  };
}

/* =====================================================================
 * REAL AI MAGIC — the line, then two films made with the studio as cards: the
 * short film and the EGT* intro. Each opens a full-screen player; phones get
 * the vertical (reel) cut, wider screens the wide one.
 * ===================================================================== */
var QUOTE_PLAIN = "Your Story Made with Real AI Magic.";
var FILMS = [
  /* the short film always plays wide, phones included (founder 2026-10-24) */
  { wide: "gyMiimc3v3w", t: "The Journey of the Coin", m: "Short film · 3:45",
    thumbWide: "/vis/film-studio/card-coin-wide.jpg" },
  { wide: "dTAJCWd1tmY", reel: "0j0ans0Hg-0", t: "EGT* · The intro", m: "Forty lives in one mark · 0:48",
    thumbWide: "/vis/film-studio/card-intro-wide.jpg", thumbReel: "/vis/film-studio/card-intro-reel.jpg" }
];

function renderMagic(FN, N) {
  return {
    n: "section", c: "egt-studio egt-fs-magic", id: "egt-fs-magic",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "blockquote", c: "egt-studio-quote__q",
          i: [
            { n: "span", c: "visually-hidden", i: QUOTE_PLAIN },
            { n: "p", c: "egt-studio-quote__line", "data-egt-quote": "true", "aria-hidden": "true",
              i: [ "Your Story Made with ", { n: "span", c: "egt__gold", i: "Real AI Magic" }, "." ] }
          ] },
        { n: "div", c: "egt-fs-films",
          i: FILMS.map(function (f, i) {
            return { n: "button", type: "button", c: "egt-fs-vcard egt-reveal egt-reveal--up", style: "--egt-i:" + i,
              "data-egt-fs-video": "true", "data-wide": f.wide, "data-reel": f.reel || undefined,
              "aria-label": "Play " + f.t.replace("EGT*", "EGT"),
              i: [
                { n: "span", c: "egt-fs-vcard__thumb",
                  i: [
                    { n: "picture",
                      i: [ f.thumbReel ? { n: "source", media: "(max-width: 36em)", srcset: f.thumbReel } : null,
                        { n: "img", src: f.thumbWide, alt: "", loading: "lazy", decoding: "async" } ].filter(Boolean) },
                    { n: "span", c: "egt-fs-vcard__play", "aria-hidden": "true" }
                  ] },
                { n: "span", c: "egt-fs-vcard__body",
                  i: [ { n: "span", c: "egt-fs-vcard__t", i: S.brand(f.t) },
                    { n: "span", c: "egt-fs-vcard__m", i: f.m } ] }
              ] };
          }) },
        { n: "p", c: "egt-studio-foot egt-fs-magic__foot",
          i: [ "Both made in the studio.", FN.mark(N.samples), " ",
            { n: "a", href: "/entertainment/the-journey-of-the-coin", i: "How the short film was made" }, "." ] }
      ] }
  };
}

/* =====================================================================
 * THE ASK, AGAIN — over a 4K still from the intro (the house look: myth and
 * wonder).
 * ===================================================================== */
function renderAsk(FN, N) {
  var form = FS.requestAccess({ id: "egt-fs-ask-req", label: [ "Request Access Today", FN.mark(N.beta) ] });
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-fs-ask", id: "egt-fs-ask",
    i: [
      S.media({ img: "/vis/film-studio/still-ax20.jpg", alt: "", parallax: 0.08 }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: { n: "div", c: "egt-glass egt-fs-ask__card egt-reveal egt-reveal--up",
          i: [
            { n: "p", c: "egt__eyebrow", i: [ FS.mark(), " Film Studio" ] },
            { n: "h2", c: "egt-fs-ask__title", i: [ "Your story is ", { n: "span", c: "egt__gold", i: "waiting." } ] },
            { n: "p", c: "egt-fs-ask__lead", i: "Request access and reserve your spot in the public beta." },
            form
          ] } }
    ]
  };
}

/* Per-surface JSON-LD: WebPage + BreadcrumbList for /studio. Site-wide
 * Organization + WebSite come from S.page. No app/offer/price/date/review type —
 * the studio is "coming", with no public price, dates, or reviews. */
function studioSchema() {
  var O = S.siteOrigin();
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/studio#webpage", url: O + "/studio",
      name: "EGT* Film Studio",
      description: "An AI film studio powered by Intrepid: one script becomes characters, scenes, voice and motion-captured performances, and cinema-ready video. Public beta.",
      about: { "@id": O + "/studio#app" },
      video: [ { "@id": O + "/studio#film-coin" }, { "@id": O + "/studio#film-intro" } ],
      isPartOf: { "@id": O + "/#website" },
      breadcrumb: { "@id": O + "/studio#breadcrumb" } },
    /* the product, in plain terms an answer engine can quote (no price, no dates:
     * it's a public beta reservation) */
    { "@type": "SoftwareApplication", "@id": O + "/studio#app", name: "EGT* Film Studio",
      applicationCategory: "MultimediaApplication", operatingSystem: "Web",
      description: "An AI film studio that turns a script into characters, scenes, voice performances and cinema-ready video in one connected pipeline. Powered by Intrepid. In public beta.",
      featureList: [ "Script breakdown into characters, scenes and shots", "Consistent characters across every scene",
        "Mood, lighting and scene control shared with your team", "Voice and motion-capture performance (the booth)",
        "Cinema-ready video with up to 10-bit precision", "Works with leading generative video models" ],
      publisher: { "@id": O + "/#organization" }, url: O + "/studio" },
    { "@type": "VideoObject", "@id": O + "/studio#film-coin", name: "The Journey of the Coin",
      description: "A 3:45 short film: one coin, three thousand years. Made with EGT* Film Studio.",
      thumbnailUrl: O + "/imgs/og/the-journey-of-the-coin.jpg", uploadDate: "2026-10-04", duration: "PT3M45S",
      embedUrl: "https://www.youtube-nocookie.com/embed/gyMiimc3v3w", contentUrl: "https://www.youtube.com/watch?v=gyMiimc3v3w" },
    { "@type": "VideoObject", "@id": O + "/studio#film-intro", name: "The Future of Film Is Human | EGT* AI Film Studio",
      description: "The EGT* intro: forty moments from forty lives gathered inside one mark. Every scene imagined by a person, shaped with AI and cut by hand.",
      thumbnailUrl: O + "/vis/film-studio/card-intro-wide.jpg", uploadDate: "2026-10-21T11:54:24-07:00", duration: "PT48S",
      embedUrl: "https://www.youtube-nocookie.com/embed/dTAJCWd1tmY", contentUrl: "https://www.youtube.com/watch?v=dTAJCWd1tmY" },
    { "@type": "BreadcrumbList", "@id": O + "/studio#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "Film Studio" }
      ] }
  ]);
}


/* =====================================================================
 * SECTION 3 — THE TURN. Dissolve the barrier; hand the visitor the director's
 * chair. No media — rides the default pages-background. Big Playfair type. The
 * barrier words (a studio / a crew / a fortune / permission) strike-through/dim
 * on scroll-in (data-egt-dissolve). Reduced-motion shows the resolved end-state.
 * ===================================================================== */
var BARRIER_WORDS = ["a studio", "a crew", "a fortune", "approval"];

function renderTurn() {
  return {
    n: "section", c: "egt-studio egt-section egt-studio-turn", id: "egt-studio-turn",
    i: { n: "div", c: "egt__shell egt-studio-turn__shell",
      i: { n: "div", c: "egt-studio-turn__inner egt-reveal", "data-egt-dissolve": "true",
        i: [
          { n: "p", c: "egt-studio-turn__eyebrow", i: "The turn" },
          { n: "h2", c: "egt-studio-turn__title",
            i: [ "You don't need millions. ", { n: "span", c: "egt__gold", i: "You need a story." } ] },
          { n: "p", c: "egt-studio-turn__line",
            i: [ "You used to need ",
              { n: "span", c: "egt-studio-turn__barrier", "data-egt-barrier": "0", i: BARRIER_WORDS[0] }, ", ",
              { n: "span", c: "egt-studio-turn__barrier", "data-egt-barrier": "1", i: BARRIER_WORDS[1] }, ", ",
              { n: "span", c: "egt-studio-turn__barrier", "data-egt-barrier": "2", i: BARRIER_WORDS[2] }, " — and someone's ",
              { n: "span", c: "egt-studio-turn__barrier", "data-egt-barrier": "3", i: BARRIER_WORDS[3] }, "." ] },
          { n: "p", c: "egt-studio-turn__body",
            i: S.brand("Now you need a story. Every choice a production makes — the cast, their voices, the locations, the light — becomes yours to make. EGT* Studio, powered by Intrepid, builds the film around your story. You direct. It comes to life.") }
        ] } }
  };
}

/* =====================================================================
 * SECTION 4 — PROOF DEMO 1: CONTINUITY. The sharpest wedge, lead visual weight.
 * A cinematic frame whose backdrop is a real scene still; pinned in the corner is
 * a small framed "cast card" (the chosen character's portrait + name). Cycling
 * the scene chips changes the backdrop while the cast-card face stays IDENTICAL
 * ("the same face, every scene"). Real frames from The Journey of the Coin.
 * Honest by construction: the cast card is a DISTINCT pinned headshot, never
 * composited into the scene. Footnoted.
 * ===================================================================== */
/* Real frames from The Journey of the Coin (founder 2026-10-05) — the doctor's
 * sequence, where the film itself held one face across four locations. */
var SCENES = [
  { id: "lobby",    name: "The lobby",       img: "/imgs/coin/j00c.jpg" },
  { id: "vessels",  name: "The vessel hall", img: "/imgs/coin/j01c.jpg" },
  { id: "ward",     name: "The ward",        img: "/imgs/coin/j05.jpg" },
  { id: "catwalk",  name: "The catwalk",     img: "/imgs/coin/j06b.jpg" }
];
/* founder 2026-10-05: the "slot machine" toggle is removed — the scenes are real
 * frames with the doctor in them, so a different cast card contradicted them. */
var CAST = [
  { id: "doctor",    name: "The Doctor",    img: "/imgs/coin/p-doctor.jpg" }
];
var HELD = CAST[0];

function renderContinuity(FN, ILLUSTRATIVE) {
  return {
    n: "section", c: "egt-studio egt-section egt-studio-sec egt-studio-cont", id: "egt-studio-continuity",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-studio-sec__head egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "Continuity, kept" },
            { n: "h2", c: "egt-section__title",
              i: [ "The same face. ", { n: "span", c: "egt__gold", i: "Every scene." }, FN.mark(ILLUSTRATIVE) ] },
            { n: "p", c: "egt-section__lead",
              i: "Cast your character once. Now watch them hold — the same face, the same look, the same person — as the world around them changes — here, the doctor from our short film The Journey of the Coin, held from a marble lobby to a hall of living vessels to a ward on emergency power. No stranger in the next shot. That's the difference between a film and a slot machine." }
          ] },
        { n: "div", c: "egt-studio-cont__stage egt-reveal egt-reveal--up",
          "data-egt-continuity": "true",
          "data-egt-held-img": HELD.img,
          "data-egt-held-name": HELD.name,
          i: [
            { n: "div", c: "egt-studio-cont__viewer",
              i: [
                { n: "div", c: "egt-studio-scene__frame has-img", "data-egt-scene-frame": "true",
                  style: "--scene-hue:0.55",
                  i: [
                    { n: "span", c: "egt-studio-scene__img", "data-egt-scene-img": "true", "aria-hidden": "true",
                      style: "background-image:url(\"" + SCENES[0].img + "\")" },
                    { n: "span", c: "egt-studio-scene__grain", "aria-hidden": "true", role: "presentation" },
                    { n: "span", c: "egt-studio-scene__frameline", "aria-hidden": "true", role: "presentation" },
                    /* the pinned cast card — a DISTINCT headshot, never composited
                     * into the scene. Server-rendered with the held face. */
                    { n: "figure", c: "egt-studio-cont__card",
                      i: [
                        { n: "span", c: "egt-studio-cont__card-img", "data-egt-castcard-img": "true", "aria-hidden": "true",
                          style: "background-image:url(\"" + HELD.img + "\")" },
                        { n: "figcaption", c: "egt-studio-cont__card-name", "data-egt-castcard-name": "true",
                          "aria-live": "polite", i: HELD.name }
                      ] },
                    { n: "p", c: "egt-studio-cont__status", "data-egt-continuity-status": "true", "aria-live": "polite",
                      i: "Scene 1 of 4 — the same face." }
                  ] }
              ] },
            { n: "div", c: "egt-studio-cont__controls",
              i: [
                { n: "p", c: "egt-studio-cont__k", i: "Change the world" },
                { n: "div", c: "egt-chiprow", role: "group", "aria-label": "Scenes",
                  i: SCENES.map(function (s, idx) {
                    return { n: "button", type: "button",
                      c: "egt-chip" + (idx === 0 ? " is-on" : ""),
                      "data-egt-scene": s.id,
                      "data-egt-scene-img": s.img,
                      "aria-pressed": idx === 0 ? "true" : "false",
                      i: s.name };
                  }) },
                { n: "p", c: "egt-studio-cont__note",
                  i: "One face, held across every scene \u2014 the same doctor from the lobby to the ward." }
              ] }
          ] },
        { n: "p", c: "egt-studio-foot egt-reveal",
          i: [ "Frames from ", { n: "a", href: "/entertainment/the-journey-of-the-coin", i: "The Journey of the Coin" }, ", made with the studio. The cast card is a real portrait from the film, never composited into these stills." ] }
      ] }
  };
}

/* =====================================================================
 * SECTION 6 — PROOF DEMO 3: CONNECTED SCRIPT. One connected pipeline: script
 * order is shot order is cut order. Scrollytelling — a gold cursor sweeps the
 * frosted script page line by line; beside it the scene viewer (backdrop = the
 * summer still) lights/settles in sync. Full script text is in the DOM (SR-safe).
 * Replays on re-entry. Footnoted.
 * ===================================================================== */
/* The battle, from the script of The Journey of the Coin (shots C01–C07). The
 * frame beside the page changes on every third line, not every line (founder
 * 2026-10-24): the armies, the centurion, then the coin in the hoofprint. */
var SCRIPT_LINES = [
  { sp: "EXT. ROMAN ROAD — RAIN", line: true, img: "/vis/film-studio/battle-c01.jpg", cap: "Shot C01 · two lines closing in the rain" },
  { sp: "A Roman road in rain; two lines closing." },
  { sp: "Hundreds of javelins arc through the rain." },
  { sp: "The centurion. The coin, set in a bronze pendant, at his throat.", img: "/vis/film-studio/battle-c02.jpg", cap: "Shot C02 · the centurion and the coin" },
  { sp: "He blocks, shoves, strikes; the pendant swings on its cord." },
  { sp: "The lines collide. The shield wall breaks." },
  { sp: "A hand tears the pendant away. The coin spins into the mud.", img: "/vis/film-studio/battle-c07.jpg", cap: "Shot C07 · after the battle" },
  { sp: "Cavalry crashes through the broken line." },
  { sp: "The battle moves on. Rain fills the hoofprint; the coin lies in it, shining." }
];

function renderConnectedScript(FN, ILLUSTRATIVE) {
  return {
    n: "section", c: "egt-studio egt-canvas egt-studio-sec egt-studio-script", id: "egt-studio-script",
    i: [
      S.media({ img: "/imgs/coin/c04.jpg", alt: "", parallax: 0.1 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-studio-script__inner",
        i: { n: "div", c: "egt-glass egt-glass--wide egt-studio-script__card egt-reveal egt-reveal--up",
          i: [
            { n: "div", c: "egt-studio-sec__head",
              i: [
                { n: "p", c: "egt__eyebrow", i: "One connected pipeline" },
                { n: "h2", c: "egt-section__title",
                  i: [ "One script. ", { n: "span", c: "egt__gold", i: "The whole film." }, FN.mark(ILLUSTRATIVE) ] },
                { n: "p", c: "egt-section__lead",
                  i: "Bring just a page or a whole screenplay. Spec the shots and your studio responds like magic. Change a line and the whole film knows. Not five disconnected tools and a homework assignment in some other editor — one connected film, from the first word to the final cut." }
              ] },
            { n: "div", c: "egt-studio-script__stage", "data-egt-reader": "true",
              i: [
                { n: "div", c: "egt-studio-script__page",
                  i: [
                    { n: "p", c: "egt-studio-script__slug", i: "The Journey of the Coin · the battle" },
                    { n: "div", c: "egt-studio-script__lines",
                      i: SCRIPT_LINES.map(function (ln, idx) {
                        var cls = "egt-studio-script__line";
                        if (ln.line) { cls += " egt-studio-script__line--slug"; }
                        if (ln.cue) { cls += " egt-studio-script__line--cue"; }
                        var ln2 = { n: "p", c: cls, "data-egt-line": String(idx), i: ln.sp };
                        if (ln.img) { ln2["data-egt-line-img"] = ln.img; ln2["data-egt-line-cap"] = ln.cap; }
                        return ln2;
                      }) }
                  ] },
                { n: "div", c: "egt-studio-script__side",
                  i: sceneViewer("script", { kicker: "Scene · exterior · rain",
                    caption: SCRIPT_LINES[0].cap, hue: 0.55, img: SCRIPT_LINES[0].img }) }
              ] },
            { n: "p", c: "egt-studio-foot",
              i: [ "From the script and the finished frames of ", { n: "a", href: "/entertainment/the-journey-of-the-coin", i: "The Journey of the Coin" }, "." ] }
          ] } }
    ]
  };
}

/* =====================================================================
 * NOT A CLIP — A WHOLE STUDIO. Hand-drawn icons like the story above (founder
 * 2026-10-24: no links, no videos), drawn on when the row comes into view.
 * ===================================================================== */
var MAKES = [
  { k: "silent", t: "Soundless generations", c: "#3FB6A8" },
  { k: "shortfilm", t: "Short films", c: "#C48E2F" },
  { k: "musicvideo", t: "Music videos", c: "#E8735A" }
];

function renderBreadth() {
  var row = [];
  MAKES.forEach(function (m, i) {
    row.push({ n: "li", c: "egt-fs-step egt-fs-step--make", i: [ icon(m.k, m.c), { n: "h3", c: "egt-fs-step__t", i: m.t } ] });
    if (i < MAKES.length - 1) { row.push({ n: "li", c: "egt-fs-link", "aria-hidden": "true", i: [ squiggle("across"), squiggle("down") ] }); }
  });
  return {
    n: "section", c: "egt-section egt-fs-breadth", id: "egt-studio-breadth",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-studio-sec__head egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "A whole studio" },
            { n: "h2", c: "egt-section__title", i: [ "Not a clip. ", { n: "span", c: "egt__gold", i: "A whole studio." } ] },
            { n: "p", c: "egt-section__lead", i: "From soundless generations to short films to full-on music videos." }
          ] },
        { n: "ol", c: "egt-fs-story__row egt-fs-breadth__row", "data-egt-fs-draw": "true", i: row }
      ] }
  };
}

/* =====================================================================
 * SECTION 8 — HONESTY. The skeptic-converter: concede what they already believe,
 * then answer. Disclosure is the brand's signature move — the * earns its meaning
 * here. S.canvas-style frosted card over the dawn horizon (an honest new day), no
 * scrim; a compact risk->answer list; the clickable *; a "Powered by Intrepid"
 * mark. No fabricated stats in the visible copy.
 * ===================================================================== */
var HONESTY_ITEMS = [
  { claim: "“It's all slop.”",
    answer: "We don't flood. Slop is abundance plus sameness plus no care. We ship curated, single-best work — craft and curation are the product." },
  { claim: "“AI can't hold a story — faces change every shot.”",
    answer: "That's the whole point of the studio: the same character, every scene (you just saw it). We design around the medium's limits — the exact craft the community respects." },
  { claim: "“Undisclosed AI is a betrayal.”",
    answer: "Agreed. We disclose at the surface, at first exposure. The asterisk is the disclosure — printed in full, not buried." },
  { claim: "“You can't copyright AI work.”",
    answer: "Correct about the raw frames — and we're candid: what you own and defend are the human layers you author, the screenplay, the edit, the arrangement. Honesty here earns more trust than any overclaim." },
  { claim: "“That AI voice — I scroll past.”",
    answer: "Voice is invented and runs on your own device, where we're the originator. Audio is where we invest, not economize." },
  { claim: "“People are losing creative jobs.”",
    answer: "The concern is real and structural. Our answer is visible human authorship plus AI as a tool — creative judgment is never outsourced." }
];

function renderHonesty() {
  return {
    n: "section", c: "egt-studio egt-canvas egt-studio-honesty egt-fs-honesty", id: "egt-studio-honesty",
    i: [
      S.media({ img: "/imgs/intrepid-dawn-horizon.jpg", alt: "", parallax: 0.08 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-studio-honesty__inner",
        i: { n: "div", c: "egt-glass egt-glass--wide egt-studio-honesty__card egt-reveal egt-reveal--up",
          i: [
            { n: "p", c: "egt__eyebrow", i: wordmark() },
            { n: "h2", c: "egt-studio-honesty__title",
              i: [ "Made with AI. ", { n: "span", c: "egt__gold", i: "Hidden from no one." } ] },
            { n: "p", c: "egt-studio-honesty__lead",
              i: "You're not against AI. You're against slop — the low-effort flood, the same face a thousand times, the work that hides what it is until you catch it. So are we. Here's exactly what this is, and what it isn't." },
            { n: "ul", c: "egt-studio-honesty__list",
              i: HONESTY_ITEMS.map(function (it) {
                return { n: "li", c: "egt-studio-honesty__item egt-reveal egt-reveal--up",
                  i: [
                    { n: "p", c: "egt-studio-honesty__claim", i: it.claim },
                    { n: "p", c: "egt-studio-honesty__answer", i: it.answer }
                  ] };
              }) },
            poweredByIntrepid()
          ] } }
    ]
  };
}

